import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import ItLoganLogo from './img/itlogan.svg';
import DevToolsProtection from './DevToolsProtection';
import { Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/login';

function getDeviceFingerprint() {
  return {
    screen: {
      width: window.screen.width,
      height: window.screen.height,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform: navigator.platform,
    userAgent: navigator.userAgent,
  };
}

function App() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vibrate, setVibrate] = useState(false);
  const [behavior, setBehavior] = useState({
    typingSpeed: null,
    mouseMoves: [],
    focusEvents: [],
    copyPaste: false,
    attempts: 0,
  });
  const startType = useRef(null);
  const mouseMoves = useRef([]);
  const focusEvents = useRef([]);
  const attempts = useRef(0);

  // Mouse movement tracking
  const handleMouseMove = (e) => {
    mouseMoves.current.push({ x: e.clientX, y: e.clientY, t: Date.now() });
  };

  // Focus/blur tracking
  const handleFocus = (field) => {
    focusEvents.current.push({ field, type: 'focus', t: Date.now() });
  };
  const handleBlur = (field) => {
    focusEvents.current.push({ field, type: 'blur', t: Date.now() });
  };

  // Typing speed
  const handleType = (e) => {
    if (!startType.current) startType.current = Date.now();
    if (e.target.name === 'username') setUsername(e.target.value);
    if (e.target.name === 'password') setPassword(e.target.value);
  };

  // Copy/paste detection
  const handlePaste = () => {
    setBehavior((b) => ({ ...b, copyPaste: true }));
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setVibrate(false);
    attempts.current += 1;
    const typingSpeed = startType.current
      ? (Date.now() - startType.current) / (username.length + password.length || 1)
      : null;
    const payload = {
      username,
      password,
      device: getDeviceFingerprint(),
      behavior: {
        typingSpeed,
        mouseMoves: mouseMoves.current,
        focusEvents: focusEvents.current,
        copyPaste: behavior.copyPaste,
        attempts: attempts.current,
      },
    };
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      
      // Always try to parse the response, regardless of status
      let data;
      try {
        data = await res.json();
      } catch (jsonError) {
        // If JSON parsing fails, but we got a response, it's likely a server error
        setTimeout(() => {
          setLoading(false);
          setError('Incorrect username or password.');
          setVibrate(true);
          setTimeout(() => setVibrate(false), 400);
          setUsername('');
          setPassword('');
          startType.current = null;
          mouseMoves.current = [];
          focusEvents.current = [];
          setBehavior((b) => ({ ...b, copyPaste: false }));
        }, 1200);
        return;
      }
      
      // Handle the response based on content, not just status code
      setTimeout(() => {
        setLoading(false);
        setError(data.message || 'Incorrect username or password.');
        setVibrate(true);
        setTimeout(() => setVibrate(false), 400);
        setUsername('');
        setPassword('');
        startType.current = null;
        mouseMoves.current = [];
        focusEvents.current = [];
        setBehavior((b) => ({ ...b, copyPaste: false }));
      }, 1200);
      
    } catch (err) {
      // Network error - likely CORS or server unavailable
      setTimeout(() => {
        setLoading(false);
        setError('Incorrect username or password.');
        setVibrate(true);
        setTimeout(() => setVibrate(false), 400);
        setUsername('');
        setPassword('');
        startType.current = null;
        mouseMoves.current = [];
        focusEvents.current = [];
        setBehavior((b) => ({ ...b, copyPaste: false }));
      }, 1200);
    }
  };

  return (
    <DevToolsProtection>
      <div className="min-h-screen flex items-center justify-center glassmorphism-bg relative overflow-hidden">
      {/* Background animated elements */}
      <div className="glassmorphism-orb glassmorphism-orb-1"></div>
      <div className="glassmorphism-orb glassmorphism-orb-2"></div>
      <div className="glassmorphism-orb glassmorphism-orb-3"></div>
      
      <div className="glassmorphism-card w-full max-w-md relative z-10">
        <img src={ItLoganLogo} alt="Logo" className="mx-auto mb-8 mt-8" style={{ width: '350px', height: '100%', objectFit: 'contain', display: 'block' }} />
        <h2 className="text-3xl font-bold text-white mb-6 text-center tracking-tight">Admin Login</h2>
        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
          <div>
            <label className="block text-gray-400 mb-2" htmlFor="username">Username</label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="off"
              required
              value={username}
              onChange={handleType}
              onFocus={() => handleFocus('username')}
              onBlur={() => handleBlur('username')}
              onPaste={handlePaste}
              className={`glassmorphism-input w-full px-4 py-3 rounded-lg text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ${vibrate ? 'animate-shake' : ''}`}
              disabled={loading}
            />
          </div>
          <div>
            <label className="block text-gray-400 mb-2" htmlFor="password">Password</label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="off"
                required
                value={password}
                onChange={handleType}
                onFocus={() => handleFocus('password')}
                onBlur={() => handleBlur('password')}
                onPaste={handlePaste}
                className={`glassmorphism-input w-full px-4 py-3 pr-12 rounded-lg text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ${vibrate ? 'animate-shake' : ''}`}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200 focus:outline-none focus:text-white z-10 bg-transparent border-none p-1"
                disabled={loading}
                aria-label={showPassword ? "Hide password" : "Show password"}
                style={{ background: 'none', border: 'none', outline: 'none' }}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" style={{ display: 'block' }} />
                ) : (
                  <Eye className="h-5 w-5" style={{ display: 'block' }} />
                )}
              </button>
            </div>
          </div>
          <button
            type="submit"
            className={`glassmorphism-button w-full py-4 rounded-xl text-white font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95 relative overflow-hidden group focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-60 ${loading ? 'cursor-not-allowed' : ''}`}
            disabled={loading}
          >
            {loading ? (
              <span className="relative z-10 flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Signing in…
              </span>
            ) : (
              <span className="relative z-10">Sign In</span>
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
          </button>
          {error && (
            <div className="text-red-400 text-center mt-2 animate-fade-in">{error}</div>
          )}
        </form>
        <div className="mt-8 text-sm text-gray-300 text-center">
          <span className="opacity-80">For authorized use only. All access attempts are monitored and logged.</span>
          <div className="mt-3 font-medium">Ramon Logan Jr. © 2025 IT Logan. All rights reserved.</div>
        </div>
      </div>
      <div
        className="fixed inset-0 z-0"
        onMouseMove={handleMouseMove}
        style={{ pointerEvents: 'none' }}
      />
    </div>
    </DevToolsProtection>
  );
}

export default App;
